<template>
  <b-modal
    id="new-data-element-wizard"
    :title="title"
    header-class="new-data-element-header"
    title-class="new-data-element-label"
    footer-class="new-data-element-footer"
    no-close-on-backdrop
    @hidden="clearForm"
  >
    <div class="wizard-container">
      <div class="content-wrapper">
        <div class="wizard-form">
          <div class="wizard-form-element">
            <label class="wizard-form-element__label">
              {{ dataElementTypeText }}
            </label>
            <div class="wizard-form-element__control">
              <b-form-select
                autofocus
                v-model="elementType"
                :options="sortedTypes"
                @change="onTypeChange"
                size="sm"
              ></b-form-select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <button class="modal-btn modal-cancel-btn" @click="hideModal()">
        {{ cancelText }}
      </button>

      <button
        class="modal-btn modal-save-btn"
        :disabled="!elementType"
        @click="createDataElement()"
      >
        {{ nextText }}
      </button>
    </template>
  </b-modal>
</template>

<script>
  import { mapState } from "vuex";
  import { v4 as uuidv4 } from "uuid";
  import store from "@/store";
  import {
    DATA_ELEMENT_TYPE,
    CALCULATE_DATA_ELEMENT_TYPES,
    TERRITORY_FEEDBACK_DATA_ELEMENT_TYPES,
    GET_DATA_ELEMENT_TYPES,
    MUTATION_NAME, CREATE_WIZARD_SEARCH_MAP
  } from "@/static/global-variables";
  import utils from "@/utils";

  export default {
    name: "NewDataElementWizard",
    props: {
      appId: String,
      title: String,
      types: Array
    },
    data() {
      return {
        elementType: ""
      };
    },
    computed: {
      ...mapState({
        apps: "apps",
        messages: "messages"
      }),
      currentApp() {
        return utils.findAppByAppId(this.apps, this.appId);
      },
      sortedTypes() {
        const filtered = this.filteredTypes();
        let getTypes = filtered.filter(word =>
          GET_DATA_ELEMENT_TYPES.includes(word.Name)
        );
        let calcTypes = filtered.filter(word =>
          CALCULATE_DATA_ELEMENT_TYPES.includes(word.Name)
        );

        if (this.currentApp.type === CREATE_WIZARD_SEARCH_MAP.Territory_Feedback_vod.contentType) {
          let feedbackTypes = filtered.filter(word =>
            TERRITORY_FEEDBACK_DATA_ELEMENT_TYPES.includes(word.Name)
          );
          return [
            {
              label: this.$t("territoryFeedback"),
              options: feedbackTypes.sort(this.compareText)
            },
            {
              label: this.$t("getData"),
              options: getTypes.sort(this.compareText)
            },
            {
              label: this.$t("calculateData"),
              options: calcTypes.sort(this.compareText)
            }
          ];
        }

        return [
          {
            label: this.$t("getData"),
            options: getTypes.sort(this.compareText)
          },
          {
            label: this.$t("calculateData"),
            options: calcTypes.sort(this.compareText)
          }
        ];
      },
      dataElementTypeText() {
        return this.getMessageText(this.messages.DATA_ELEMENT_TYPE);
      },
      cancelText() {
        return this.getMessageText(this.messages.CANCEL);
      },
      nextText() {
        return this.getMessageText(this.messages.NEXT);
      }
    },
    methods: {
      hideModal() {
        this.$bvModal.hide("new-data-element-wizard");
      },
      clearForm() {
        this.elementType = "";
      },
      filteredTypes() {
        let filtered = this.types.filter(
          t =>
            t.value !== DATA_ELEMENT_TYPE.DROPDOWN &&
            t.value !== DATA_ELEMENT_TYPE.DATEPICKER
        );
        if (utils.hasVeevaMsgDataElement(this.currentApp)) {
          filtered = this.types.filter(t => {
            return t.value !== DATA_ELEMENT_TYPE.VEEVA_MESSAGES;
          });
        }
        return filtered;
      },
      compareText(a, b) {
        if (a.text < b.text) {
          return -1;
        }
        if (a.text > b.text) {
          return 1;
        }
        return 0;
      },
      createDataElement() {
        let defaultFields = {};
        if (this.elementType === "DATA_EL_TODAY") {
          defaultFields = { Today: { dataType: "Date" } };
        } else if (this.elementType === "DATA_EL_NOW") {
          defaultFields = { Now: { dataType: "DateTime" } };
        }
        let elementType = this.elementType;
        if (this.elementType === "DATA_EL_SORT") {
          elementType = "DATA_EL_SORT_LIST"; // update correct type since label name does not match
        }
        if (this.elementType === "DATA_EL_FILTER") {
          elementType = "DATA_EL_FILTER_LIST"; // update correct type since label name does not match
        }
        const dataElement = {
          appId: this.appId,
          id: uuidv4(),
          name: "",
          description: "",
          type: elementType,
          fields: defaultFields,
          notReady: true
        };
        if (
          this.elementType !== "DATA_EL_TODAY" &&
          this.elementType !== "DATA_EL_NOW"
        ) {
          dataElement.object = "";
          dataElement.obj = null;
          dataElement.selectedField = null;
          dataElement.limit = null;
        }
        if (this.elementType === "DATA_EL_MERGE_LIST") {
          dataElement.sources = [];
        }

        store.commit(MUTATION_NAME.EDIT_DATA_PALETTES, dataElement);

        this.hideModal();
        this.$emit("de-created", dataElement.id);
      },
      onTypeChange(val) {
        this.elementType = val;
      }
    }
  };
</script>

<style scoped>
  .wizard-form-element {
    display: block;
    padding: 0.25rem;
    width: 100%;
    flex-basis: 100%;
    margin-bottom: 0.5rem;
    position: relative;
    min-width: 0;
    flex-direction: column;
  }

  @media (min-width: 48em) {
    .wizard-form-element__label {
      float: left;
      max-width: calc(33% - 1.25rem);
      flex-basis: calc(33% - 1.25rem);
      margin-bottom: 0;
      position: relative;
      z-index: 1;
    }

    .wizard-form-element__control {
      clear: none;
    }
  }

  .wizard-form-element__label {
    float: left;
    overflow-wrap: break-word;
    word-wrap: break-word;
    -webkit-hyphens: auto;
    -ms-hyphens: auto;
    hyphens: auto;
    display: inline-block;
    color: #3e3e3c;
    font-size: 0.75rem;
    padding-right: 0.5rem;
    padding-top: 0.25rem;
    margin-bottom: 0.125rem;
  }

  .wizard-form-element__control {
    position: relative;
  }

  .modal-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0;
    background: transparent;
    background-clip: border-box;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    line-height: 1.875rem;
    color: #0070d2;
    -webkit-appearance: none;
    white-space: normal;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    text-decoration: none;
  }

  .modal-btn[disabled] {
    background-color: var(--DISABLED_BTN_GREY);
    border-color: var(--DISABLED_BTN_GREY);
    color: var(--WHITE);
  }

  .modal-save-btn {
    margin-left: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    background-color: #0070d2;
    border: 1px solid #0070d2;
    color: #fff;
  }

  .modal-cancel-btn {
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    border: 1px solid #dddbda;
    background-color: #fff;
  }
</style>
